// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useMutation } from '@apollo/client';
import { clientLogger } from '@seedplanner/logger';
import { PLANTING_STATUS } from '@seedplanner/shared-utils';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { useNavigate } from 'react-router';
import { useGating, useUserContext } from '../../context/UserContext';
import { MenuItem } from '../../lib/base';
import { AddCircleOutlineIcon, CoronavirusIcon, DeleteIcon, EditIcon } from '../../lib/icons';
import * as routes from '../../routes';
import type {
  DeletePlantingGroupMutation,
  DeletePlantingGroupMutationVariables,
} from '../../types/__generated__/graphql';
import { Confirmation } from '../common/dailog';
import { Options } from '../common/dropdown';
import AddTraysToPlantingGroupDialog from './AddTraysToPlantingGroupDialog';
import EditPlantingGroupNotesDialog from './EditPlantingGroupNotesDialog';
import RecordLostTraysDialog from './RecordLostTraysDialog';

const DELETE_PLANTING_MUTATION = gql`
  mutation DeletePlantingGroup($planting_group_id: Int!) {
    deletePlantingGroup(input: { planting_group_id: $planting_group_id }) {
      plantings_deleted {
        id
      }
      tasks_deleted {
        id
      }
      updated_recurring_plantings {
        id
        recurring_options {
          exceptions
        }
        updated_at
      }
    }
  }
`;

interface Props {
  readonly plantingIds: readonly number[];
  readonly plantingGroupStatus: string;
  readonly hasBeenPlanted: boolean;
  readonly onSuccess: (shouldRefetchSelf: boolean) => void;
  readonly useMoreIcon?: boolean;
}

export default function PlantingGroupOptions({
  plantingIds,
  plantingGroupStatus,
  hasBeenPlanted,
  onSuccess,
  useMoreIcon,
}: Props) {
  const { canUseBasic } = useGating();
  const { permissions } = useUserContext();
  const navigate = useNavigate();
  const [optionsClose, setOptionsClose] = React.useState(false);
  const [editNotesOpen, setEditNotesOpen] = React.useState(false);
  const [increaseTraysOpen, setIncreaseTraysOpen] = React.useState(false);
  const [deletePlantingGroupOpen, setDeletePlantingGroupOpen] = React.useState(false);
  const [recordLostTraysOpen, setRecordLostTraysOpen] = React.useState(false);

  const [deletePlantingGroup, { loading: deletePlantingGroupLoading }] = useMutation<
    DeletePlantingGroupMutation,
    DeletePlantingGroupMutationVariables
  >(DELETE_PLANTING_MUTATION);

  const onSubmitDeletePlantingGroup = () => {
    void deletePlantingGroup({
      variables: {
        planting_group_id: plantingIds[0],
      },
      update: (cache) => {
        cache.modify({
          fields: {
            harvests(_existingGroup, { DELETE }) {
              return DELETE;
            },
            plantings(_existingGroup, { DELETE }) {
              return DELETE;
            },
            taskGroup(_existingGroup, { DELETE }) {
              return DELETE;
            },
            task_groups(_existingGroup, { DELETE }) {
              return DELETE;
            },
            tasks(_existingGroup, { DELETE }) {
              return DELETE;
            },
            plantingGroup(_existingGroup, { DELETE }) {
              return DELETE;
            },
            planting_groups(_existingGroup, { DELETE }) {
              return DELETE;
            },
            plantingTimeGroup(_existingGroup, { DELETE }) {
              return DELETE;
            },
          },
        });
      },
      onCompleted: (data) => {
        if (data !== undefined && data.deletePlantingGroup != null) {
          enqueueSnackbar('Deleted planting group', { variant: 'success' });
          navigate(routes.PLANTINGS);
          setDeletePlantingGroupOpen(false);
          setOptionsClose(true);
          onSuccess(false);
        } else {
          enqueueSnackbar('Something went wrong!', {
            variant: 'error',
            autoHideDuration: 10000,
          });
        }
      },
      onError: (errorIn) => {
        enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
        clientLogger.error({ title: 'delete_planting_group_error', message: errorIn.message });
      },
    });
  };

  React.useEffect(() => {
    setOptionsClose(false);
  });

  const editNotes = (
    <MenuItem
      key="edit-planting-notes"
      onClick={() => {
        setEditNotesOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <EditIcon />
      Edit Notes
    </MenuItem>
  );
  const increaseTrays = (
    <MenuItem
      key="increase-trays"
      onClick={() => {
        setIncreaseTraysOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic || plantingGroupStatus === PLANTING_STATUS.COMPLETED}
    >
      <AddCircleOutlineIcon />
      Add Trays
    </MenuItem>
  );
  const deletePlantingGroupItem = (
    <MenuItem
      key="delete-planting-group"
      onClick={() => {
        setDeletePlantingGroupOpen(true);
      }}
      disableRipple
      // Can only delete a group if it's not planned
      disabled={
        !(plantingGroupStatus === PLANTING_STATUS.IN_PROGRESS || plantingGroupStatus === PLANTING_STATUS.COMPLETED) ||
        !canUseBasic
      }
    >
      <DeleteIcon />
      Delete Planting Group
    </MenuItem>
  );
  const recordLostTrays = (
    <MenuItem
      key="record-lost-trays"
      onClick={() => {
        setRecordLostTraysOpen(true);
      }}
      disableRipple
      disabled={!hasBeenPlanted || !canUseBasic || plantingGroupStatus === PLANTING_STATUS.COMPLETED}
    >
      <CoronavirusIcon />
      Record Lost Trays
    </MenuItem>
  );

  const options = [editNotes];
  if (permissions.can_edit_plantings) {
    options.push(increaseTrays);
    options.push(recordLostTrays);
    options.push(deletePlantingGroupItem);
  }

  return (
    <>
      <Options useMoreIcon={useMoreIcon} close={optionsClose} options={options} />
      {editNotesOpen && (
        <EditPlantingGroupNotesDialog
          isOpen={editNotesOpen}
          onClose={() => {
            setEditNotesOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={() => {
            setEditNotesOpen(false);
            setOptionsClose(true);
            onSuccess(false);
          }}
          plantingIds={plantingIds}
        />
      )}
      {increaseTraysOpen && (
        <AddTraysToPlantingGroupDialog
          isOpen={increaseTraysOpen}
          onClose={() => {
            setIncreaseTraysOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={() => {
            setIncreaseTraysOpen(false);
            setOptionsClose(true);
            onSuccess(false);
          }}
          plantingIds={plantingIds}
        />
      )}
      {deletePlantingGroupOpen && (
        <Confirmation
          open={deletePlantingGroupOpen}
          disableSubmit={deletePlantingGroupLoading}
          title="Permanently Delete Planting Group"
          description="Are you sure you want to permanently delete this planting group? This group of trays will be permanently deleted along with their tasks. It will be as if these trays never existed in your farm. This action cannot be undone."
          onClose={() => {
            setDeletePlantingGroupOpen(false);
            setOptionsClose(true);
          }}
          onSubmit={() => {
            onSubmitDeletePlantingGroup();
          }}
        />
      )}
      {recordLostTraysOpen && (
        <RecordLostTraysDialog
          isOpen={recordLostTraysOpen}
          onClose={() => {
            setRecordLostTraysOpen(false);
            setOptionsClose(true);
          }}
          plantingId={plantingIds[0]}
          onSuccess={() => {
            setRecordLostTraysOpen(false);
            setOptionsClose(true);
            onSuccess(true);
          }}
        />
      )}
    </>
  );
}
